import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Wheat, TrendingUp, ShieldCheck, Sparkles,
  BarChart3, Users2, Bell, LineChart,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, fmtPrice, fmtPct, changeColor } from "@/lib/api";

const FEATURES = [
  { icon: LineChart, title: "Live market intelligence", body: "Streaming futures data across 8 launch commodities with 1D → 5Y depth, technicals and volatility signals." },
  { icon: Sparkles, title: "AI Buy / Hold / Wait signals", body: "Claude Sonnet 5 fuses your forecast layer, weather & macro signals into structured verdicts with rationale." },
  { icon: Users2, title: "Vendor benchmarking", body: "Curated global suppliers with basis vs market, grade, MOQ and lead time — negotiated rates for Enterprise." },
  { icon: Bell, title: "Precision alerts", body: "Threshold, % move and volatility alerts push to email + mobile. Never miss a procurement window." },
  { icon: BarChart3, title: "Analyst notes", body: "Human-authored weekly reports, sentiment-tagged, gated by tier for premium desks." },
  { icon: ShieldCheck, title: "Enterprise hedging (soon)", body: "Exposure calculator + futures/options overview + scenario simulator for risk-off procurement." },
];

const COMMODITIES = ["WHEAT","CORN","SOYBEAN","COTTON","COFFEE","SUGAR","RICE","PALM_OIL"];

export default function Landing() {
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/commodities/quotes").then(({data}) => setQuotes(data)).catch(()=>{});
  }, []);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* nav */}
      <div className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary"><Wheat className="h-4 w-4" /></div>
            <div>
              <div className="font-display text-sm font-bold">AgriTerminal <span className="text-primary">Pro</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Buying Consultant</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">Log in</Link>
            <Button data-testid="nav-signup" onClick={() => navigate("/register")} className="rounded-full">Start free</Button>
          </div>
        </div>
      </div>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_top,black,transparent_60%)]" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 lg:grid-cols-[1.15fr_1fr] lg:gap-16 lg:px-6 lg:py-24">
          <div>
            <Badge variant="outline" className="mb-5 rounded-full border-primary/40 bg-primary/10 text-primary uppercase tracking-widest text-[10px]">
              For traders, procurement & farmers
            </Badge>
            <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Time your agri buys with <span className="text-primary">terminal-grade</span> intelligence.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
              A Bloomberg-lite consultant for agri-commodity procurement. Live prices, AI-driven Buy/Hold/Wait signals across
              wheat, corn, soybeans, cotton, coffee, sugar, rice and palm oil — plus curated vendors and analyst notes.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" data-testid="hero-cta-signup" onClick={() => navigate("/register")} className="rounded-full">
                Start free — no card <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-cta-pricing" onClick={() => navigate("/register")} className="rounded-full">
                See pricing
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <span>· 8 launch commodities</span>
              <span>· Claude Sonnet 5</span>
              <span>· Stripe billing</span>
            </div>
          </div>

          {/* Right terminal card */}
          <div className="relative rounded-xl border border-border/60 bg-card/70 p-4 shadow-2xl shadow-primary/5 backdrop-blur-md">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                <span className="h-2 w-2 animate-pulse rounded-full bg-primary" /> Live market strip
              </div>
              <div className="text-[10px] font-mono text-muted-foreground">Demo feed</div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {(quotes.length ? quotes : COMMODITIES.map(c => ({code:c,name:c,price:0,change_pct:0}))).slice(0,8).map(q => (
                <Link
                  key={q.code}
                  to="/register"
                  data-testid={`hero-quote-${q.code.toLowerCase()}`}
                  className="group flex items-center justify-between rounded-md border border-border/50 bg-background/60 px-3 py-2 transition hover:border-primary/40"
                >
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.code}</div>
                    <div className="num text-sm font-semibold">{fmtPrice(q.price, 2)}</div>
                  </div>
                  <div className={`text-xs font-mono ${changeColor(q.change_pct)}`}>{fmtPct(q.change_pct)}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* features */}
      <section className="border-t border-border/60 bg-card/30">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary">01 · Product</div>
              <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Built for people who buy at scale.</h2>
            </div>
            <TrendingUp className="hidden h-8 w-8 text-primary/60 lg:block" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-${i}`} className="rounded-lg border border-border/60 bg-background/60 p-5 transition hover:border-primary/40">
                <f.icon className="h-5 w-5 text-primary" />
                <div className="mt-4 font-display text-base font-semibold">{f.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* cta */}
      <section className="border-t border-border/60">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 px-4 py-16 text-center lg:px-6">
          <h3 className="font-display text-3xl font-bold tracking-tight">Stop guessing. Start timing.</h3>
          <p className="max-w-lg text-sm leading-relaxed text-muted-foreground">
            Set up your watchlist in under a minute. Free tier includes delayed prices, weekly summaries and analyst notes.
          </p>
          <Button size="lg" data-testid="footer-cta" onClick={() => navigate("/register")} className="mt-2 rounded-full">
            Create your terminal <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="border-t border-border/60 py-6 text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Market insight, not financial advice · © 2026 AgriTerminal Pro
        </div>
      </section>
    </div>
  );
}
